'use client';

import { useId } from 'react';
import type { ChangeEventHandler } from 'react';
import Button from 'components/shared/Buttons/BaseButton';
import { formatCurrency } from 'components/Widget/shared/currency';
import { DURATION_FIELD, FUND_OPTIONS, NO_TIMELINE, RESULT_LABELS } from './constants';
import { formatMonths } from './emergencyFund';
import type { EmergencyFundResult, FundGoalKey } from './types';

interface EmergencyFundCalculatorResultProps {
  result: EmergencyFundResult;
  goalKey: FundGoalKey;
  onGoalKeyChange: (goalKey: FundGoalKey) => void;
  onStartOver: () => void;
}

const isFundGoalKey = (value: string): value is FundGoalKey => FUND_OPTIONS.some(option => option.key === value);

/** Chimekit's text-label-sm is parsed as a color by tailwind-merge. */
const PILL = 'mt-3 inline-block rounded-full bg-bg-primary px-4 py-2 text-label-saans';

const LABEL = 'text-body-saans';

export const EmergencyFundCalculatorResult = ({
  result,
  goalKey,
  onGoalKeyChange,
  onStartOver,
}: EmergencyFundCalculatorResultProps) => {
  const durationId = useId();
  const hintId = `${durationId}-hint`;
  const handleDurationChange: ChangeEventHandler<HTMLSelectElement> = event => {
    if (isFundGoalKey(event.target.value)) onGoalKeyChange(event.target.value);
  };

  return (
    <div className="mt-6 flex flex-col gap-8">
      <dl className="flex flex-col items-center">
        <dt className={LABEL}>{RESULT_LABELS.monthlyExpenses}</dt>
        <dd className={PILL}>{formatCurrency(result.monthlyExpenses)}</dd>
      </dl>

      {/* Column-major keeps legacy's pairing: what you plan to do on the left, what it gets you on the right. */}
      <dl className="grid gap-6 sm:grid-flow-col sm:grid-rows-[auto_auto]">
        <div>
          <dt>
            <label htmlFor={durationId} className={LABEL}>
              {DURATION_FIELD.label}
            </label>
          </dt>
          <dd>
            <select
              id={durationId}
              aria-describedby={hintId}
              value={goalKey}
              onChange={handleDurationChange}
              className="mt-3 h-16 w-full rounded-lg border border-content-secondary bg-bg-primary px-6 text-body-saans text-content-primary outline-none transition-shadow hover:ring-[6px] hover:ring-bg-tertiary focus:border-content-primary focus:ring-[6px] focus:ring-bg-tertiary">
              {FUND_OPTIONS.map(option => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>
            <p id={hintId} className="mt-2 text-body-xs text-content-secondary">
              {DURATION_FIELD.hint}
            </p>
          </dd>
        </div>

        <div>
          <dt className={LABEL}>{RESULT_LABELS.monthlyContribution}</dt>
          <dd className={PILL}>
            {result.monthlyContribution === 0 ? NO_TIMELINE : `${formatCurrency(result.monthlyContribution)}/month`}
          </dd>
        </div>

        <div>
          <dt className={LABEL}>{RESULT_LABELS.goal}</dt>
          <dd className={PILL}>{formatCurrency(result.goal)}</dd>
        </div>

        <div>
          <dt className={LABEL}>{RESULT_LABELS.monthsToGoal}</dt>
          <dd className={PILL}>{result.monthsToGoal === null ? NO_TIMELINE : formatMonths(result.monthsToGoal)}</dd>
        </div>
      </dl>

      <Button type="button" variant="outline" className="sm:w-40 sm:self-center" onClick={onStartOver}>
        Start over
      </Button>
    </div>
  );
};
